<script setup lang="ts">
//#region imports
import { nextTick, ref } from 'vue';
import { useApi } from '@/api';
import type { GetAffiliatesListWithStatsData, GetAffiliatesListWithStatsResponse } from '@/types/openapi-ts';
import type { EmitsToListeners } from '@/types/core';
import type { UiTableNewField } from '@/types/table';
//#endregion

//#region types
interface Props {
  groupName:  string,
  groupId: string,
}

interface  Emits {
  'close': [],
}

export interface RotationGroupAffiliatesParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>
}
//#endregion

//#region define
const props = defineProps<Props>();
const emit = defineEmits<Emits>();
//#endregion

//#region const
const fields: UiTableNewField<GetAffiliatesListWithStatsResponse['payload'][number]>[] = [
  {
    name: 'email',
    minWidth: '300',
    link: getAffiliateLink,
  },
];
//#endregion

//#region composables
const api = useApi();
//#endregion

//#region state
const isVisible = ref(false);

const isDataLoading = ref(false);
const tableData = ref<GetAffiliatesListWithStatsResponse['payload']>([]);

const page = ref(1);
const limit = ref(50);
const count = ref(0);
//#endregion

//#region methods
async function open() {
  await getAffiliatesWithStats();
  await nextTick();
  isVisible.value = true;
}

function handleClose() {
  emit('close');
  isVisible.value = false;
}

function getAffiliateLink(row: GetAffiliatesListWithStatsResponse['payload'][number]) {
  return `/affiliates/${row.id}/info`;
}

async function getAffiliatesWithStats() {
  isDataLoading.value = true;

  const query: GetAffiliatesListWithStatsData['query'] = {
    sort_column: 'email',
    sort_dir: 'asc',
    limit: limit.value,
    offset: limit.value * page.value - limit.value,
    platform_service_rotation_group_id: props.groupId,
  };

  try {
    const { data: { payload, misc }} = await api.getAffiliatesWithStats(query);
    tableData.value = payload;
    count.value = misc?.count || 0;
  } finally {
    isDataLoading.value = false;
  }
}

function pageChange(newPage: number, newSize: number) {
  if (page.value !== newPage || limit.value !== newSize) {
    page.value = newPage;
    limit.value = newSize;
    getAffiliatesWithStats();
  }
}
//#endregion

defineExpose({
  open,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="rotation-group-affiliates-popup"
    :title="props.groupName"
    :width="480"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="rotation-group-affiliates-popup__body">
        <div class="t:space-y-8">
          <div class="t:flex t:justify-between t:items-center">
            <UiLabel
              class="t:mb-8"
              :label="$t('affiliates.name')"
              :font-size="16"
              bold
            />
            <UiPagination
              :page="page"
              :page-size="limit"
              :count="count"
              @page-change="pageChange"
            />
          </div>
          <UiTableNew
            v-loading="isDataLoading"
            :data="tableData"
            :fields="fields"
            class="rotation-group-affiliates-popup__table"
            is-static
            disable-sort
            :padding="0"
            i18n-path="affiliates.list"
            element-loading-custom-class="data-loader-spinner"
          />
        </div>
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex">
        <UiButton
          type="positive"
          filled
          size="big"
          icon="check"
          @click="handleClose"
        >
          {{ $t(`domainManagement.popup.close`) }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>

<style lang="scss">
.rotation-group-affiliates-popup {
  &__table {
    max-height: 60vh;
    overflow: auto;
  }
}
</style>
